import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getItem, getItemFilters, listItems, searchItemsWithAi, updateItemThumbnail } from '@/api/items';
import { favoritesKeys } from '@/lib/query-keys';
import type { AiSearchResponse, ItemListItem } from '@/types';
import { normalizeItemFilterSearch, type ItemFilterSearch } from '@/lib/validation';
import { itemKeys } from '@/lib/query-keys';

interface UseItemsOptions {
  enabled?: boolean;
}

function normalizeItemsQueryError(error: unknown, fallbackMessage: string): Error | null {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string' && '' !== error.trim()) {
    return new Error(error);
  }

  if (error) {
    return new Error(fallbackMessage);
  }

  return null;
}

export function useItems(filters: ItemFilterSearch, options: UseItemsOptions = {}) {
  const normalizedFilters = useMemo(
    () => normalizeItemFilterSearch(filters),
    [filters.query, filters.tags, filters.type, filters.ui],
  );
  const listQueryKey = useMemo(
    () => itemKeys.list(normalizedFilters),
    [normalizedFilters.query, normalizedFilters.tags, normalizedFilters.type, normalizedFilters.ui],
  );
  const query = useInfiniteQuery({
    queryKey: listQueryKey,
    queryFn: ({ pageParam }) => listItems(normalizedFilters, pageParam),
    initialPageParam: 1,
    retry: false,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      return nextPage <= lastPage.totalPages ? nextPage : undefined;
    },
    enabled: options.enabled ?? true,
  });

  const items = useMemo<ItemListItem[]>(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const initialError = useMemo(
    () => (!query.data ? normalizeItemsQueryError(query.error, "Couldn't load items.") : null),
    [query.data, query.error],
  );

  const loadMoreError = useMemo(
    () => (
      query.data && query.isFetchNextPageError
        ? normalizeItemsQueryError(query.error, "Couldn't load more items.")
        : null
    ),
    [query.data, query.error, query.isFetchNextPageError],
  );

  const loadNextPage = useCallback(async () => {
    if (!query.hasNextPage || query.isFetchingNextPage) {
      return;
    }

    await query.fetchNextPage();
  }, [query.fetchNextPage, query.hasNextPage, query.isFetchingNextPage]);

  return {
    ...query,
    items,
    initialError,
    loadMoreError,
    loadNextPage,
    retryLoadMore: loadNextPage,
  };
}

export function useItem(itemId: number | null | undefined) {
  return useQuery({
    queryKey: itemKeys.detail(itemId ?? 0),
    queryFn: () => getItem(itemId ?? 0),
    enabled: typeof itemId === 'number' && itemId > 0,
  });
}

export function useItemFilters() {
  return useQuery({
    queryKey: itemKeys.filters,
    queryFn: getItemFilters,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAiSearch(filters: ItemFilterSearch, options: UseItemsOptions = {}) {
  const normalizedFilters = useMemo(
    () => normalizeItemFilterSearch(filters),
    [filters.query, filters.tags, filters.type, filters.ui],
  );
  const aiSearchQueryKey = useMemo(
    () => itemKeys.aiSearch(normalizedFilters),
    [normalizedFilters.query, normalizedFilters.tags, normalizedFilters.type],
  );

  return useQuery<AiSearchResponse>({
    queryKey: aiSearchQueryKey,
    queryFn: () => searchItemsWithAi(normalizedFilters),
    enabled: Boolean(normalizedFilters.query) && (options.enabled ?? true),
    retry: false,
  });
}

interface UpdateItemThumbnailInput {
  itemId: number;
  file: File;
}

export function useUpdateItemThumbnail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, file }: UpdateItemThumbnailInput) => updateItemThumbnail(itemId, file),
    onSuccess: (updatedItem) => {
      queryClient.setQueryData(itemKeys.detail(updatedItem.id), updatedItem);
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === itemKeys.all[0] && query.queryKey[1] === 'list',
      });
      void queryClient.invalidateQueries({ queryKey: favoritesKeys.list });
    },
  });
}
